import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { http, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex">
    {/* left: form panel on solid background — nothing overlaps the inputs */}
    <div className="w-full lg:w-[46%] bg-[#F8F6F1]/85 backdrop-blur-md flex flex-col">
      <div className="px-6 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-0.5">
          <img src="/logo.png" alt="BidMark Quotes logo" className="h-12 w-12 rounded-xl object-contain" />
          <span className="font-serif text-2xl font-bold tracking-tight text-[#1B3A4B]">BidMark Quotes</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <h1 className="font-serif text-3xl font-bold text-[#1C1915]">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-[#5C564E]">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
    {/* right: photo panel (decorative, desktop only) */}
    <div className="hidden lg:block relative flex-1">
      <img src="/login-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#132A37]/55" />
      <div className="absolute bottom-10 left-10 right-10 text-white">
        <p className="font-serif text-3xl font-bold leading-tight drop-shadow">Know what to charge.</p>
        <p className="mt-2 text-white/85 max-w-md">Real prices off the web — a low, typical, and high for any job, with sources.</p>
      </div>
    </div>
  </div>
);

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5 mb-4">
    <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">{label}</Label>
    <Input {...props} />
  </div>
);

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Log in" subtitle="Your quotes and searches stay on your account.">
      <form onSubmit={submit}>
        <Field label="Email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <Field label="Password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {err && <p data-testid="login-error" className="text-sm text-[#9E2A2B] mb-3">{err}</p>}
        <Button data-testid="login-submit" disabled={busy} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-between text-sm">
        <Link data-testid="forgot-link" to="/forgot-password" className="text-[#5C564E] underline underline-offset-2">
          Forgot password?
        </Link>
        <Link data-testid="to-register" to="/register" className="text-[#1B3A4B] font-medium">
          Create account
        </Link>
      </div>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", company_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(form);
      navigate("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Create account" subtitle="An email and a password. That's it.">
      <form onSubmit={submit}>
        <Field label="Company name" data-testid="register-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Harbor Line Plumbing" />
        <Field label="Email" data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <Field label="Password" data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        {err && <p data-testid="register-error" className="text-sm text-[#9E2A2B] mb-3">{err}</p>}
        <Button data-testid="register-submit" disabled={busy} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <p className="mt-4 text-sm text-center text-[#5C564E]">
        Already have one?{" "}
        <Link data-testid="to-login" to="/login" className="text-[#1B3A4B] font-medium">
          Log in
        </Link>
      </p>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Reset password" subtitle="We'll send one link, good for 30 minutes.">
      {sent ? (
        <div data-testid="forgot-sent" className="text-[#5C564E]">
          <p>If that email is registered, a reset link has been sent. Check your inbox.</p>
          <Link to="/login" className="mt-4 inline-block text-[#1B3A4B] font-medium">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit}>
          <Field label="Email" data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Button data-testid="forgot-submit" disabled={busy} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-11">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
          </Button>
          <Link to="/login" className="mt-4 block text-center text-sm text-[#5C564E] underline underline-offset-2">
            Back to sign in
          </Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser, refreshCredits } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/auth/reset-password", { token, password });
      setUser(data);
      await refreshCredits();
      toast.success("Password saved. You're logged in.");
      navigate("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="New password" subtitle="Saving it logs you in and ends the link.">
      <form onSubmit={submit}>
        <Field label="New password" data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <Field label="Confirm password" data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        {err && <p data-testid="reset-error" className="text-sm text-[#9E2A2B] mb-3">{err}</p>}
        <Button data-testid="reset-submit" disabled={busy} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save password"}
        </Button>
        <Link to="/forgot-password" className="mt-4 block text-center text-sm text-[#5C564E] underline underline-offset-2">
          Request a new link
        </Link>
      </form>
    </Shell>
  );
}
